import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';
import * as XLSX from 'xlsx';
import './CustomsDepotsViewer.css';

const CustomsDepotsViewer = () => {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const initRef = useRef(false);
    const depotsDataRef = useRef([]);
    const isMounted = useRef(false);
    const dataLoaded = useRef(false);
    const [codeFilter, setCodeFilter] = useState('');
    const [portFilter, setPortFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [depotTypeFilter, setDepotTypeFilter] = useState(''); // New state for Depot Type filter
    const [depotsData, setDepotsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const isDrawing = useRef(false);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const [showExpired, setShowExpired] = useState(false);
    const navigate = useNavigate();
    const isDev = process.env.NODE_ENV !== 'production';

    const expectedHeaders = useMemo(
        () => [
            'Establishment Code',
            'Start Date',
            'End Date',
            'Depot Type',
            'Port',
            'AQIS Premise',
            'Establishment Name',
        ],
        []
    );

    const applyStyles = () => {
        $('#customsDepotsTable').addClass('table-visible');
    };

    const excelDateToJSDate = useCallback((serial) => {
        if (!serial || isNaN(serial) || serial === '#VALUE!' || serial === '') {
            return null;
        }
        try {
            const excelEpoch = new Date(1899, 11, 30);
            const days = Number(serial);
            const date = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
            if (isDev) {
                console.log(`Parsed serial ${serial} to ${date.toISOString().split('T')[0]}`);
            }
            return date;
        } catch (error) {
            if (isDev) {
                console.warn(`Invalid date format for serial: ${serial}`, error);
            }
            return null;
        }
    }, [isDev]); // Dependency on isDev

    useEffect(() => {
        isMounted.current = true;
    
        if (dataLoaded.current || depotsDataRef.current.length > 0) {
            if (isDev) {
                console.log('Data already loaded or component unmounted, skipping fetch.');
            }
            setIsLoading(false);
            return;
        }
    
        const loadDepotsData = async () => {
            try {
                setIsLoading(true);
                if (isDev) {
                    console.log('Fetching customsdepots.xlsx...');
                }
                const response = await fetch('/customsdepots.xlsx');
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const arrayBuffer = await response.arrayBuffer();
                if (isDev) {
                    console.log('Excel file fetched, parsing...');
                }
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                if (!firstSheet) {
                    throw new Error('No sheets found in Excel file');
                }
    
                let loadedDepotsData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
                if (isDev) {
                    console.log(`Parsed ${loadedDepotsData.length} rows from Excel`);
                }
                loadedDepotsData = loadedDepotsData.map((row, index) => {
                    const newRow = {};
                    expectedHeaders.forEach((header) => {
                        let value = row[header] !== undefined ? String(row[header]).trim() : '';
                        if (header === 'End Date') {
                            newRow['rawEndDate'] = value;
                            if (value && !isNaN(value) && value !== '#VALUE!') {
                                try {
                                    const date = XLSX.SSF.parse_date_code(Number(value));
                                    value = `${date.d}/${date.m}/${date.y}`;
                                } catch (error) {
                                    if (isDev) {
                                        console.warn(`Row ${index + 2}: Invalid date for ${header}: ${value}`);
                                    }
                                    value = 'N/A';
                                }
                            } else {
                                value = value === '#VALUE!' ? 'N/A' : value || 'N/A';
                            }
                        } else if (header === 'Start Date') {
                            if (value && !isNaN(value) && value !== '#VALUE!') {
                                try {
                                    const date = XLSX.SSF.parse_date_code(Number(value));
                                    value = `${date.d}/${date.m}/${date.y}`;
                                } catch (error) {
                                    if (isDev) {
                                        console.warn(`Row ${index + 2}: Invalid date for ${header}: ${value}`);
                                    }
                                    value = 'N/A';
                                }
                            } else {
                                value = value === '#VALUE!' ? 'N/A' : value || 'N/A';
                            }
                        }
                        newRow[header] = value;
                    });
                    return newRow;
                });
    
                if (isMounted.current) {
                    depotsDataRef.current = loadedDepotsData;
                    setDepotsData(loadedDepotsData);
                    if (isDev) {
                        console.log(`Loaded ${loadedDepotsData.length} depots into state`);
                    }
                    dataLoaded.current = true;
                }
                setIsLoading(false);
            } catch (error) {
                console.error('Error loading Customs Depots data:', error);
                alert('Failed to load Customs Depots data. Please check the console for details.');
                if (isMounted.current) {
                    depotsDataRef.current = [];
                    setDepotsData([]);
                }
                setIsLoading(false);
            }
        };
    
        loadDepotsData();
    
        return () => {
            isMounted.current = false;
            if (dataTableRef.current && !document.getElementById('customsDepotsTable')) {
                try {
                    dataTableRef.current.destroy();
                    if (isDev) {
                        console.log('DataTable destroyed on cleanup');
                    }
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
            }
            $.fn.dataTable.ext.search = [];
        };
    }, [expectedHeaders, isDev]);

    useEffect(() => {
        if (!depotsData.length || !tableRef.current || isTableInitialized) return;
    
        if (isDev) {
            console.log('Initializing DataTable with', depotsData.length, 'rows');
        }
    
        if (initRef.current && dataTableRef.current) {
            try {
                dataTableRef.current.destroy();
                if (isDev) {
                    console.log('Previous DataTable destroyed');
                }
            } catch (error) {
                console.error('Error destroying DataTable:', error);
            }
            initRef.current = false;
        }
    
        applyStyles();
    
        try {
            dataTableRef.current = $(tableRef.current).DataTable({
                data: depotsData,
                columns: expectedHeaders.map((header) => ({
                    title: header,
                    data: header,
                    render: (data, type, row, meta) => {
                        return data || '';
                    },
                })),
                columnDefs: [
                    { width: '100px', targets: 0 }, // Establishment Code
                    { width: '80px', targets: 1 }, // Start Date
                    { width: '80px', targets: 2 }, // End Date
                    { width: '80px', targets: 3 }, // Depot Type
                    { width: '80px', targets: 4 }, // Port
                    { width: '80px', targets: 5 }, // AQIS Premise
                    { width: 'auto', targets: 6 }, // Establishment Name
                ],
                pageLength: 15,
                lengthMenu: [15, 25, 50, 100],
                searching: true,
                ordering: true,
                order: [], // Preserve original order from XLSX
                responsive: true,
                language: {
                    search: '',
                    searchPlaceholder: 'Filter records...',
                    lengthMenu: 'Show _MENU_ entries',
                },
                dom: '<"table-header"l>t<"table-footer"ip>',
                deferRender: true,
            });
            initRef.current = true;
            dataTableRef.current.on('draw', () => {
                applyStyles();
                isDrawing.current = false;
                if (isDev) {
                    console.log('DataTable drawn');
                }
            });
            setIsTableInitialized(true);
            if (isDev) {
                console.log('DataTable initialized successfully');
            }
        } catch (error) {
            console.error('Error initializing DataTable:', error);
            alert('Failed to initialize table. Please check the console for details.');
            setIsLoading(false);
        }
    }, [depotsData, expectedHeaders, isDev, isTableInitialized]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const portOptions = useMemo(() => {
        const ports = [...new Set(depotsData.map((row) => row['Port']))]
            .filter((port) => port)
            .sort();
        return ['', ...ports];
    }, [depotsData]);

    const depotTypeOptions = useMemo(() => {
        const depotTypes = [...new Set(depotsData.map((row) => row['Depot Type']))]
            .filter((type) => type)
            .sort();
        return ['', ...depotTypes];
    }, [depotsData]);

    useEffect(() => {
        if (!dataTableRef.current || !isTableInitialized) return;
    
        const timeoutId = setTimeout(() => {
            if (isDrawing.current) return;
            isDrawing.current = true;
            if (isDev) {
                console.log('Applying filters:', { codeFilter, portFilter, nameFilter, depotTypeFilter, showExpired });
            }
    
            $.fn.dataTable.ext.search = [];
    
            const filterFunction = (settings, data, dataIndex, rowData, counter) => {
                if (settings.nTable.id !== 'customsDepotsTable') return true;
                const code = String(data[0] || '').toLowerCase();
                const depotType = String(data[3] || '').toLowerCase();
                const port = String(data[4] || '').toLowerCase(); // Adjusted index due to Sub-Type removal
                const name = String(data[6] || '').toLowerCase(); // Adjusted index due to Sub-Type removal
                const rawEndDate = rowData['rawEndDate'] || '';
                const endDate = excelDateToJSDate(rawEndDate);
                const currentDate = new Date();
                const codeFilterValue = codeFilter.toLowerCase().trim();
                const depotTypeFilterValue = depotTypeFilter.toLowerCase().trim();
                const portFilterValue = portFilter.toLowerCase().trim();
                const nameFilterValue = nameFilter.toLowerCase().trim();
    
                let codeMatch = true;
                let depotTypeMatch = true;
                let portMatch = true;
                let nameMatch = true;
                let dateMatch = true;
    
                if (codeFilterValue) {
                    codeMatch = code.includes(codeFilterValue);
                }
    
                if (depotTypeFilterValue) {
                    depotTypeMatch = depotType === depotTypeFilterValue;
                }
    
                if (portFilterValue) {
                    portMatch = port === portFilterValue;
                }
    
                if (nameFilterValue) {
                    nameMatch = name.includes(nameFilterValue);
                }
    
                if (!showExpired) {
                    dateMatch = !endDate || endDate >= currentDate;
                }
    
                const isMatch = codeMatch && depotTypeMatch && portMatch && nameMatch && dateMatch;
                if (!isMatch && !showExpired && endDate && isDev) {
                    console.log(`Filtered out row: ${rowData['Establishment Code']}, End Date: ${rawEndDate} (${endDate ? endDate.toISOString().split('T')[0] : 'N/A'})`);
                }
                return isMatch;
            };
    
            $.fn.dataTable.ext.search.push(filterFunction);
    
            try {
                dataTableRef.current
                    .column(0)
                    .search('')
                    .column(3)
                    .search('')
                    .column(4) // Adjusted index due to Sub-Type removal
                    .search('')
                    .column(6) // Adjusted index due to Sub-Type removal
                    .search('')
                    .draw();
                if (isDev) {
                    console.log('DataTable filters applied');
                }
            } catch (error) {
                console.error('Error during DataTable filter draw:', error);
                isDrawing.current = false;
            }
        }, 400);
    
        return () => clearTimeout(timeoutId);
    }, [codeFilter, depotTypeFilter, portFilter, nameFilter, showExpired, isTableInitialized, isDev, excelDateToJSDate]);

    const handleCodeFilterChange = useCallback((e) => {
        setCodeFilter(e.target.value);
    }, []);

    const handleDepotTypeFilterChange = useCallback((e) => {
        setDepotTypeFilter(e.target.value);
    }, []);

    const handlePortFilterChange = useCallback((e) => {
        setPortFilter(e.target.value);
    }, []);

    const handleNameFilterChange = useCallback((e) => {
        setNameFilter(e.target.value);
    }, []);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = useCallback(() => {
        if (dataTableRef.current) {
            setCodeFilter('');
            setDepotTypeFilter('');
            setPortFilter('');
            setNameFilter('');
            setShowExpired(false);
            dataTableRef.current
                .column(0)
                .search('')
                .column(3)
                .search('')
                .column(4) // Adjusted index due to Sub-Type removal
                .search('')
                .column(6) // Adjusted index due to Sub-Type removal
                .search('')
                .draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
            if (isDev) {
                console.log('Table reset');
            }
        }
    }, [isDev]);

    const toggleExpiredDepots = useCallback(() => {
        if (isDev) {
            console.log('Toggling expired depots, current showExpired:', showExpired);
        }
        setShowExpired((prev) => !prev);
    }, [showExpired, isDev]);

    return (
        <div className="customs-depots-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" />
                    <h1>Customs Depots</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">
                            Home
                        </Link>
                        <Link to="/app" className="nav-item">
                            Classify Goods
                        </Link>
                        <Link to="/customs-chat" className="nav-item">
                            Customs Chat
                        </Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item">
                                    Tariff Viewer
                                </Link>
                                <Link to="/tco-viewer" className="dropdown-item">
                                    TCO Viewer
                                </Link>
                                <Link to="/precedent-viewer" className="dropdown-item">
                                    Precedent Viewer
                                </Link>
                                <Link to="/ahecc-viewer" className="dropdown-item">
                                    AHECC Viewer
                                </Link>
                                <Link to="/schedule4-viewer" className="dropdown-item">
                                    Schedule 4 Viewer
                                </Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item">
                                    Refund Reason Codes
                                </Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item">
                                    GST Exemption Codes
                                </Link><Link to="/customs-depots-viewer" className="dropdown-item active">
                                    Customs Depots Codes
                                </Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item">
                                    Quarantine Depot Codes
                                </Link>
                                <Link to="/history" className="dropdown-item">
                                    History
                                </Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">
                            Settings
                        </Link>
                        <span className="nav-item logout" onClick={handleLogout}>
                            Logout
                        </span>
                    </nav>
                </div>    
            </header>
            <div className="main-content">
                <div className="table-panel">
                    {isLoading ? (
                        <div className="loading">Loading depots data...</div>
                    ) : (
                        <>
                            <div className="table-header">
                                <div id="customFilter">
                                    <input
                                        type="text"
                                        placeholder="Filter Establishment Code..."
                                        value={codeFilter}
                                        onChange={handleCodeFilterChange}
                                        className="custom-filter-input code-filter-input"
                                    />
                                    <select
                                        value={depotTypeFilter}
                                        onChange={handleDepotTypeFilterChange}
                                        className="custom-filter-select depot-type-filter-select"
                                    >
                                        <option value="">All Depot Types</option>
                                        {depotTypeOptions.map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={portFilter}
                                        onChange={handlePortFilterChange}
                                        className="custom-filter-select port-filter-select"
                                    >
                                        <option value="">All Ports</option>
                                        {portOptions.map((port) => (
                                            <option key={port} value={port}>
                                                {port}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        placeholder="Filter Establishment Name..."
                                        value={nameFilter}
                                        onChange={handleNameFilterChange}
                                        className="custom-filter-input name-filter-input"
                                    />
                                </div>
                                <div className="table-buttons">
                                    <button id="toggleExpiredBtn" onClick={toggleExpiredDepots}>
                                        {showExpired ? 'Hide Expired Depots' : 'Show Expired Depots'}
                                    </button>
                                    <button id="resetTableBtn" onClick={resetTable}>
                                        Reset Table
                                    </button>
                                </div>
                            </div>
                            <table id="customsDepotsTable" className="display" ref={tableRef}></table>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomsDepotsViewer;